import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../../../components/Loader';
import ApiService from '../../../services/ApiService';
import './Broadcasts.css';

/**
 * Compose and send a notification to app users.
 *
 * Nothing is delivered inline — submitting queues the broadcast and the backend
 * cron worker sends it in batches, so the screen returns immediately whether the
 * audience is one user or every user.
 */

const CHANNELS = [
    { value: 'push', label: 'Push only', icon: 'bi bi-phone', hint: 'Sent to the mobile app and saved to the in-app inbox.' },
    { value: 'email', label: 'Email only', icon: 'bi bi-envelope', hint: 'Sent to the address on the user account.' },
    { value: 'both', label: 'Push + Email', icon: 'bi bi-broadcast', hint: 'Both channels for every recipient.' },
];

/** `datetime-local` needs a local "YYYY-MM-DDTHH:mm", not an ISO/UTC string. */
const toLocalInputValue = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const AddUpdate = () => {
    const navigate = useNavigate();

    const [isLoader, setIsLoader] = useState(false);
    const [users, setUsers] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [userSearch, setUserSearch] = useState('');
    const [formData, setFormData] = useState({
        title: '',
        body: '',
        channel: 'push',
        audience: 'all',
        user_ids: [],
        schedule: 'now',          // 'now' | 'later'
        scheduled_at: '',
    });

    useEffect(() => {
        getRecipients();
    }, []);

    const getRecipients = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/broadcasts/recipients',
            });
            const data = response.data;
            if (data.status) {
                setUsers(data.data || []);
                setTotalUsers(data.total_users || 0);
            }
        } catch (error) {
            // Handled by ApiService.
        }
    };

    const filteredUsers = useMemo(() => {
        const term = userSearch.trim().toLowerCase();
        if (!term) return users;
        return users.filter(
            (user) =>
                (user.name || '').toLowerCase().includes(term) ||
                (user.email || '').toLowerCase().includes(term)
        );
    }, [users, userSearch]);

    const selectedUsers = useMemo(
        () => users.filter((user) => formData.user_ids.includes(user._id)),
        [users, formData.user_ids]
    );

    // A push-only send silently misses anyone without a registered device, so
    // surface the count before it goes out rather than in the delivery report.
    const unreachableCount = useMemo(() => {
        if (formData.channel === 'email') return 0;
        const pool = formData.audience === 'all' ? users : selectedUsers;
        return pool.filter((user) => !user.has_device_token).length;
    }, [formData.channel, formData.audience, users, selectedUsers]);

    const recipientCount = formData.audience === 'all' ? totalUsers : formData.user_ids.length;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const toggleUser = (userId) => {
        setFormData((prev) => ({
            ...prev,
            user_ids: prev.user_ids.includes(userId)
                ? prev.user_ids.filter((id) => id !== userId)
                : [...prev.user_ids, userId],
        }));
    };

    const toggleAllVisible = () => {
        const visibleIds = filteredUsers.map((user) => user._id);
        const allSelected = visibleIds.every((id) => formData.user_ids.includes(id));
        setFormData((prev) => ({
            ...prev,
            user_ids: allSelected
                ? prev.user_ids.filter((id) => !visibleIds.includes(id))
                : [...new Set([...prev.user_ids, ...visibleIds])],
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            toast.error('Title is required');
            return;
        }
        if (!formData.body.trim()) {
            toast.error('Message is required');
            return;
        }
        if (formData.audience === 'selected' && formData.user_ids.length === 0) {
            toast.error('Select at least one user');
            return;
        }
        if (formData.schedule === 'later') {
            if (!formData.scheduled_at) {
                toast.error('Pick a date and time to schedule');
                return;
            }
            if (new Date(formData.scheduled_at) <= new Date()) {
                toast.error('Scheduled time must be in the future');
                return;
            }
        }

        setIsLoader(true);
        try {
            const response = await ApiService.request({
                method: 'POST',
                url: 'admin/broadcasts',
                data: {
                    title: formData.title,
                    body: formData.body,
                    channel: formData.channel,
                    audience: formData.audience,
                    user_ids: formData.audience === 'selected' ? formData.user_ids : [],
                    // The input is local time; toISOString hands the server UTC.
                    scheduled_at:
                        formData.schedule === 'later'
                            ? new Date(formData.scheduled_at).toISOString()
                            : null,
                },
            });

            if (response.data.status) {
                toast.success(response.data.message);
                navigate('/admin/broadcasts');
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Form submission failed:', error);
        } finally {
            setIsLoader(false);
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">New Notification</h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/broadcasts" className="text-muted text-hover-primary">Send Notifications</Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-lg-8">
                                <div className="card mb-5">
                                    <div className="card-body py-6">
                                        <div className="mb-6">
                                            <label className="required form-label">Title</label>
                                            <input
                                                type="text"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleChange}
                                                className="form-control form-control-solid"
                                                placeholder="e.g. Courts closed this Friday"
                                                maxLength={120}
                                            />
                                        </div>

                                        <div className="mb-2">
                                            <label className="required form-label">Message</label>
                                            <textarea
                                                name="body"
                                                value={formData.body}
                                                onChange={handleChange}
                                                className="form-control form-control-solid"
                                                rows={5}
                                                placeholder="Write the message your users will receive…"
                                                maxLength={1000}
                                            />
                                            <div className="form-text text-end">{formData.body.length} / 1000</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Audience */}
                                <div className="card mb-5">
                                    <div className="card-body py-6">
                                        <label className="form-label">Send to</label>
                                        <div className="row g-3 mb-4">
                                            <div className="col-md-6">
                                                <div
                                                    className={`bc-option ${formData.audience === 'all' ? 'bc-option-active' : ''}`}
                                                    onClick={() => setFormData((p) => ({ ...p, audience: 'all' }))}
                                                >
                                                    <i className="bi bi-people-fill"></i>
                                                    <div>
                                                        <div className="bc-option-title">All users</div>
                                                        <div className="bc-option-hint">{totalUsers} active users</div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div
                                                    className={`bc-option ${formData.audience === 'selected' ? 'bc-option-active' : ''}`}
                                                    onClick={() => setFormData((p) => ({ ...p, audience: 'selected' }))}
                                                >
                                                    <i className="bi bi-person-check-fill"></i>
                                                    <div>
                                                        <div className="bc-option-title">Selected users</div>
                                                        <div className="bc-option-hint">{formData.user_ids.length} selected</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {formData.audience === 'selected' && (
                                            <>
                                                <div className="d-flex gap-2 mb-3">
                                                    <input
                                                        type="text"
                                                        value={userSearch}
                                                        onChange={(e) => setUserSearch(e.target.value)}
                                                        className="form-control form-control-solid"
                                                        placeholder="Search name or email…"
                                                    />
                                                    <button type="button" className="btn btn-light-primary text-nowrap" onClick={toggleAllVisible}>
                                                        Select all
                                                    </button>
                                                </div>
                                                <div className="bc-user-list">
                                                    {filteredUsers.length > 0 ? filteredUsers.map((user) => (
                                                        <label key={user._id} className="bc-user-row">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input me-3"
                                                                checked={formData.user_ids.includes(user._id)}
                                                                onChange={() => toggleUser(user._id)}
                                                            />
                                                            <span className="flex-grow-1">
                                                                <span className="d-block text-dark fw-semibold">{user.name || 'Unnamed user'}</span>
                                                                <span className="d-block text-muted fs-7">{user.email || 'No email'}</span>
                                                            </span>
                                                            {!user.has_device_token && (
                                                                <span className="badge badge-light-warning" title="No device registered — push will not reach this user">
                                                                    No device
                                                                </span>
                                                            )}
                                                        </label>
                                                    )) : (
                                                        <div className="text-center text-muted py-6">No users found</div>
                                                    )}
                                                </div>
                                                {users.length >= 500 && (
                                                    <div className="form-text">
                                                        Showing the first 500 users. Use "All users" to reach everyone.
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4">
                                {/* Channel */}
                                <div className="card mb-5">
                                    <div className="card-body py-6">
                                        <label className="form-label">Channel</label>
                                        {CHANNELS.map((channel) => (
                                            <div
                                                key={channel.value}
                                                className={`bc-option mb-3 ${formData.channel === channel.value ? 'bc-option-active' : ''}`}
                                                onClick={() => setFormData((p) => ({ ...p, channel: channel.value }))}
                                            >
                                                <i className={channel.icon}></i>
                                                <div>
                                                    <div className="bc-option-title">{channel.label}</div>
                                                    <div className="bc-option-hint">{channel.hint}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Schedule */}
                                <div className="card mb-5">
                                    <div className="card-body py-6">
                                        <label className="form-label">Delivery</label>
                                        <div className="d-flex gap-3 mb-3">
                                            <label className="form-check form-check-custom">
                                                <input
                                                    type="radio"
                                                    className="form-check-input"
                                                    name="schedule"
                                                    value="now"
                                                    checked={formData.schedule === 'now'}
                                                    onChange={handleChange}
                                                />
                                                <span className="form-check-label ms-2">Send now</span>
                                            </label>
                                            <label className="form-check form-check-custom">
                                                <input
                                                    type="radio"
                                                    className="form-check-input"
                                                    name="schedule"
                                                    value="later"
                                                    checked={formData.schedule === 'later'}
                                                    onChange={handleChange}
                                                />
                                                <span className="form-check-label ms-2">Schedule</span>
                                            </label>
                                        </div>

                                        {formData.schedule === 'later' && (
                                            <input
                                                type="datetime-local"
                                                name="scheduled_at"
                                                value={formData.scheduled_at}
                                                onChange={handleChange}
                                                min={toLocalInputValue(new Date())}
                                                className="form-control form-control-solid"
                                            />
                                        )}

                                        <div className="form-text mt-3">
                                            Notifications are delivered in batches by a background job, so a
                                            large send continues even after you leave this page.
                                        </div>
                                    </div>
                                </div>

                                {/* Summary */}
                                <div className="card mb-5">
                                    <div className="card-body py-6">
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">Recipients</span>
                                            <span className="fw-bold text-dark">{recipientCount}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">Channel</span>
                                            <span className="fw-bold text-dark">
                                                {CHANNELS.find((c) => c.value === formData.channel)?.label}
                                            </span>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span className="text-muted">When</span>
                                            <span className="fw-bold text-dark">
                                                {formData.schedule === 'now'
                                                    ? 'Immediately'
                                                    : formData.scheduled_at
                                                        ? new Date(formData.scheduled_at).toLocaleString()
                                                        : '—'}
                                            </span>
                                        </div>

                                        {unreachableCount > 0 && (
                                            <div className="alert alert-warning d-flex align-items-center mt-4 mb-0 py-3">
                                                <i className="bi bi-exclamation-triangle-fill me-3"></i>
                                                <span className="fs-7">
                                                    {unreachableCount} of these users have no registered device and
                                                    will not receive the push.
                                                </span>
                                            </div>
                                        )}

                                        <div className="d-flex gap-3 mt-5">
                                            <Link to="/admin/broadcasts" className="btn btn-light flex-grow-1">Cancel</Link>
                                            <button type="submit" className="btn btn-base flex-grow-1" disabled={isLoader}>
                                                {formData.schedule === 'now' ? 'Send' : 'Schedule'}
                                            </button>
                                        </div>
                                        {isLoader && <Loader text="Submitting" size="30" className="text-center mt-3" />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddUpdate;
